import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { DataService } from '../services/data.service';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private readonly APP_SETTINGS_KEY = 'appSettings';
  private readonly USER_PREFERENCES_KEY = 'userPreferences';
  private readonly API_CONFIG_KEY = 'apiConfig';
  private readonly GENERIC_STORAGE_PREFIX = 'generic_';

  private _appSettings = new BehaviorSubject<any>({});
  public appSettings$: Observable<any> = this._appSettings.asObservable();

  private _userPreferences = new BehaviorSubject<any>({});
  public userPreferences$: Observable<any> = this._userPreferences.asObservable();

  private _apiConfig = new BehaviorSubject<any>({});
  public apiConfig$: Observable<any> = this._apiConfig.asObservable();

  constructor(private dataService: DataService) {
    this._initializeAllData();
  }

  private async _initializeAllData(): Promise<void> {
    await Promise.all([
      this._loadAppSettings(),
      this._loadUserPreferences(),
      this._loadApiConfig(),
    ]);
    // Don't fetch here - DataService.preloadEssentialData() already loads configurations
    // this._fetchAndCacheApiConfig();
  }

  private _getDefaultAppSettings(): any {
    return {
      appName: environment.APP_NAME,
      appLanguage: 'en',
      appDirection: 'ltr',
      appNotification: false,
      appDarkMode: false,
      currentUser: null,
      testEnabled: null,
      setTheme: 'light3',
      loginData: {
        phone: 0,
        password: null,
      },
    };
  }

  private _getDefaultUserPreferences(): any {
    return {
      lastActivity: null,
      favoriteColor: 'blue',
      savedSearches: [],
    };
  }

  private _getDefaultApiConfig(): any {
    return {
      organization: {},
      cachedAt: null,
    };
  }

  private async _loadAppSettings(): Promise<void> {
    const data = await this._loadFromStorage(this.APP_SETTINGS_KEY);
    if (data) {
      this._appSettings.next({ ...this._getDefaultAppSettings(), ...data });
    } else {
      await this._saveToStorage(this.APP_SETTINGS_KEY, this._getDefaultAppSettings());
    }
  }

  private async _loadUserPreferences(): Promise<void> {
    const data = await this._loadFromStorage(this.USER_PREFERENCES_KEY);
    if (data) {
      this._userPreferences.next({ ...this._getDefaultUserPreferences(), ...data });
    } else {
      await this._saveToStorage(this.USER_PREFERENCES_KEY, this._getDefaultUserPreferences());
    }
  }

  private async _loadApiConfig(): Promise<void> {
    const data = await this._loadFromStorage(this.API_CONFIG_KEY);
    if (data?.organization) {
      this._apiConfig.next(data);
    } else {
      await this._saveToStorage(this.API_CONFIG_KEY, this._getDefaultApiConfig());
    }
  }

  private _fetchAndCacheApiConfig(): void {
    this.dataService.getData('configurations').subscribe(async (response: any) => {
      if (response?.organization) {
        await this.updateApiConfig(response.organization);
      }
    });
  }

  private async _loadFromStorage(key: string): Promise<any> {
    try {
      const { value } = await Preferences.get({ key });
      return value ? JSON.parse(value) : null;
    } catch (error) {
      console.error(`Error loading ${key}:`, error);
      return null;
    }
  }

  private async _saveToStorage(key: string, data: any): Promise<void> {
    try {
      await Preferences.set({
        key,
        value: JSON.stringify(data),
      });
    } catch (error) {
      console.error(`Error saving ${key}:`, error);
    }
  }

  get appSettingsSync(): any {
    return this._appSettings.getValue();
  }

  public async updateAppSettings(newSettings: any): Promise<void> {
    const updated = { ...this.appSettingsSync, ...newSettings };
    this._appSettings.next(updated);
    await this._saveToStorage(this.APP_SETTINGS_KEY, updated);
  }

  public async clearAppSettings(): Promise<void> {
    const defaults = this._getDefaultAppSettings();
    this._appSettings.next(defaults);
    await this._saveToStorage(this.APP_SETTINGS_KEY, defaults);
  }

  get userPreferencesSync(): any {
    return this._userPreferences.getValue();
  }

  public async updateUserPreferences(newPreferences: any): Promise<void> {
    const updated = { ...this.userPreferencesSync, ...newPreferences };
    this._userPreferences.next(updated);
    await this._saveToStorage(this.USER_PREFERENCES_KEY, updated);
  }

  public async clearUserPreferences(): Promise<void> {
    const defaults = this._getDefaultUserPreferences();
    this._userPreferences.next(defaults);
    await this._saveToStorage(this.USER_PREFERENCES_KEY, defaults);
  }

  public configurationsSync(): any {
    return this._apiConfig.getValue();
  }

  public async updateApiConfig(newConfig: any): Promise<void> {
    const updated = {
      ...this.configurationsSync(),
      organization: newConfig,
      cachedAt: new Date().toISOString(),
    };
    this._apiConfig.next(updated);
    await this._saveToStorage(this.API_CONFIG_KEY, updated);
  }

  public async cacheApiConfig(configData: any): Promise<void> {
    if (configData?.organization) {
      await this.updateApiConfig(configData.organization);
      console.log('[ConfigService] API config cached');
    }
  }

  public async refreshApiConfig(): Promise<any> {
    console.log('[ConfigService] Refreshing API config from server...');
    return new Promise((resolve, reject) => {
      this.dataService.getData('configurations').subscribe({
        next: async (response: any) => {
          if (response?.organization) {
            await this.updateApiConfig(response.organization);
            console.log('[ConfigService] API config refreshed successfully');
            resolve(response);
          } else {
            reject(new Error('Invalid configuration response'));
          }
        },
        error: (err) => {
          console.error('[ConfigService] Failed to refresh API config:', err);
          reject(err);
        }
      });
    });
  }

  public async clearApiConfig(): Promise<void> {
    const defaults = this._getDefaultApiConfig();
    this._apiConfig.next(defaults);
    await this._saveToStorage(this.API_CONFIG_KEY, defaults);
  }

  public async setGenericStorage(key: string, value: any): Promise<void> {
    const storageKey = `${this.GENERIC_STORAGE_PREFIX}${key}`;
    await this._saveToStorage(storageKey, value);
  }

  public async getGenericStorage(key: string): Promise<any> {
    const storageKey = `${this.GENERIC_STORAGE_PREFIX}${key}`;
    return await this._loadFromStorage(storageKey);
  }

  public async removeGenericStorage(key: string): Promise<void> {
    const storageKey = `${this.GENERIC_STORAGE_PREFIX}${key}`;
    try {
      await Preferences.remove({ key: storageKey });
    } catch (error) {
      console.error(`Error removing ${storageKey}:`, error);
    }
  }

  get currentSettings(): any {
    return this.appSettingsSync;
  }

  public async setStorage(key: string, value: any): Promise<void> {
    return this.setGenericStorage(key, value);
  }

  public async getStorage(key: string): Promise<any> {
    return this.getGenericStorage(key);
  }

  public async removeStore(key: string): Promise<void> {
    return this.removeGenericStorage(key);
  }

  public async updateConfigurations(config: any): Promise<void> {
    return this.updateApiConfig(config);
  }
}

/*
 * ============================================================================
 * CONFIG SERVICE USAGE GUIDE
 * ============================================================================
 *
 * This service manages 4 types of persistent storage with different purposes:
 *
 * 1. APP SETTINGS (appSettings)
 *    - Purpose: User-configurable app preferences (language, theme, notifications)
 *    - Scope: Persists across sessions
 *    - Defaults: Set from environment + hardcoded defaults
 *    - Use Case: Language selection, dark mode toggle, login credentials
 *
 * 2. USER PREFERENCES (userPreferences)
 *    - Purpose: User-specific app behavior preferences
 *    - Scope: Persists across sessions
 *    - Defaults: Hardcoded defaults
 *    - Use Case: Favorite colors, saved searches, user activity tracking
 *
 * 3. API CONFIG (apiConfig)
 *    - Purpose: Server configuration fetched once per session
 *    - Scope: Cached from API, persists across sessions as fallback
 *    - Defaults: Empty organization object
 *    - Use Case: Organization settings, feature flags, phone codes, currencies
 *    - Auto-fetched: On service initialization from /configurations endpoint
 *
 * 4. GENERIC STORAGE (generic_*)
 *    - Purpose: Temporary or custom data storage
 *    - Scope: Individual key-value pairs
 *    - Defaults: None (returns null if not found)
 *    - Use Case: Session data, temporary caches, feature-specific storage
 *
 * ============================================================================
 * USAGE EXAMPLES
 * ============================================================================
 *
 * IMPORT IN COMPONENT:
 * constructor(private configService: ConfigService) {}
 *
 * --- APP SETTINGS USAGE ---
 *
 * // Synchronous access (immediate, no await needed)
 * const currentLanguage = this.configService.appSettingsSync.appLanguage;
 * console.log('Current Language:', currentLanguage);
 *
 * // Reactive subscription (updates whenever settings change)
 * this.configService.appSettings$.subscribe(settings => {
 *   console.log('Settings Updated:', settings);
 *   this.applyTheme(settings.setTheme);
 * });
 *
 * // Update settings (persists to storage)
 * await this.configService.updateAppSettings({
 *   appLanguage: 'ar',
 *   appDirection: 'rtl',
 *   appDarkMode: true
 * });
 *
 * // Clear to defaults
 * await this.configService.clearAppSettings();
 *
 * --- USER PREFERENCES USAGE ---
 *
 * // Synchronous access
 * const savedSearches = this.configService.userPreferencesSync.savedSearches;
 *
 * // Reactive subscription
 * this.configService.userPreferences$.subscribe(prefs => {
 *   console.log('User Preferences:', prefs);
 * });
 *
 * // Update preferences
 * await this.configService.updateUserPreferences({
 *   favoriteColor: 'red',
 *   savedSearches: ['pizza', 'burger']
 * });
 *
 * // Clear to defaults
 * await this.configService.clearUserPreferences();
 *
 * --- API CONFIG USAGE ---
 *
 * // Synchronous access (cached from API)
 * const config = this.configService.configurationsSync();
 * const phoneCode = config.organization.dialing_code;
 * const currency = config.organization.currency;
 *
 * // Reactive subscription (updates when API config is fetched)
 * this.configService.apiConfig$.subscribe(config => {
 *   console.log('API Config Updated:', config.organization);
 *   this.phoneCode = config.organization.dialing_code;
 * });
 *
 * // Manually update (usually done automatically)
 * await this.configService.updateApiConfig({
 *   dialing_code: '965',
 *   currency: 'KWD',
 *   is_referral_enabled: 1
 * });
 *
 * // Clear cached config
 * await this.configService.clearApiConfig();
 *
 * --- GENERIC STORAGE USAGE ---
 *
 * // Store custom data
 * await this.configService.setGenericStorage('sessionToken', 'abc123xyz');
 * await this.configService.setGenericStorage('userCart', {
 *   items: [1, 2, 3],
 *   total: 50
 * });
 *
 * // Retrieve custom data
 * const token = await this.configService.getGenericStorage('sessionToken');
 * const cart = await this.configService.getGenericStorage('userCart');
 *
 * // Remove custom data
 * await this.configService.removeGenericStorage('sessionToken');
 *
 * ============================================================================
 * BEST PRACTICES
 * ============================================================================
 *
 * 1. Use sync getters (appSettingsSync) for immediate access in templates
 * 2. Use observables ($) for reactive updates in component logic
 * 3. API Config is auto-fetched on init, no manual fetch needed
 * 4. Generic storage is for temporary/custom data, not core app state
 * 5. Always await update/clear operations to ensure persistence
 * 6. Combine with OnDestroy to unsubscribe from observables
 *
 * EXAMPLE COMPONENT:
 *
 * import { Component, OnDestroy } from '@angular/core';
 * import { Subject } from 'rxjs';
 * import { takeUntil } from 'rxjs/operators';
 *
 * @Component({
 *   selector: 'app-example',
 *   template: `<p>Language: {{ language }}</p>`
 * })
 * export class ExampleComponent implements OnDestroy {
 *   language: string;
 *   private destroy$ = new Subject<void>();
 *
 *   constructor(private configService: ConfigService) {
 *     this.language = this.configService.appSettingsSync.appLanguage;
 *
 *     this.configService.appSettings$
 *       .pipe(takeUntil(this.destroy$))
 *       .subscribe(settings => {
 *         this.language = settings.appLanguage;
 *       });
 *   }
 *
 *   async changeLanguage(lang: string) {
 *     await this.configService.updateAppSettings({ appLanguage: lang });
 *   }
 *
 *   ngOnDestroy() {
 *     this.destroy$.next();
 *     this.destroy$.complete();
 *   }
 * }
 */
